// client/src/i18n/index.ts — V1: Romanian ONLY. 04 §9.6.1 (NORMATIVE).
//
// ─────────────────────────────────────────────────────────────────────────────
// ADDING A LANGUAGE (doc 05 I18N-001 criterion 1; the Appendix B recipe, item I18N-003)
//
//   1. add `src/i18n/english.json`, mirroring romanian.json KEY FOR KEY;
//   2. add ONE entry to `resources` below and ONE to `supportedLngs`.
//
// That is the whole change, and it is the whole point of this item. It stays a two-line
// edit only while three V1 guarantees hold, so none of them is negotiable:
//
//   • zero hardcoded strings — every user-visible string is a t() key (§9.6.5, enforced by
//     `i18next/no-literal-string` in eslint.config.js AND by `npm run i18n:check`);
//   • Zod schemas stay language-free — `error:` carries a `validation.*` KEY that FormField
//     resolves at render (§5.4), so no schema is touched when a language is added;
//   • the key tree is module-namespaced (§9.6.2) — a translator gets whole modules, and a
//     missing namespace is visible as a hole rather than as scattered absences.
//
// This file is the ONLY place a language is registered. Nothing else in the app may call
// `i18n.changeLanguage`, read a language from storage, or branch on a locale.
// ─────────────────────────────────────────────────────────────────────────────

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import romanian from './romanian.json';

// `void`: init() is synchronous in this configuration — there is no backend and no detector to
// wait for — but it still returns a promise, and an unhandled one is a lint error (D-324).
void i18n.use(initReactI18next).init({
  resources: { ro: { translation: romanian } }, // THE single V1 resource (§2.1)
  lng: 'ro',
  fallbackLng: 'ro',
  supportedLngs: ['ro'],
  interpolation: { escapeValue: false }, // React escapes; i18next must not double-escape
  returnNull: false,

  // Romanian plurals are `_one / _few / _other` (§9.6.4) and come from Intl.PluralRules via
  // i18next's own v4 JSON format. Nothing to configure — but see the „de" rule: at ≥20 the
  // `_other` form carries it („21 de zile"), which is why all three forms are always authored.

  // A missing key renders as its own path — „rent.columns.amount" in front of a landlord — so
  // it must be LOUD while a developer is looking. `saveMissing` is what arms missingKeyHandler;
  // with no backend registered it writes nothing anywhere, it only calls the handler below.
  // Both are off in production: the real guard is `npm run i18n:check`, which fails the build
  // before a missing key can ship (§9.6.5).
  saveMissing: import.meta.env.DEV,
  missingKeyHandler: import.meta.env.DEV
    ? (_lngs: readonly string[], ns: string, key: string) => {
        console.error(`[i18n] missing key: ${ns}:${key}`);
      }
    : false,

  // i18next-browser-languagedetector is in the version matrix (FOUNDATION_BRIEF §6) and is
  // installed, but it is deliberately NOT wired: detection is meaningless while supportedLngs
  // has one entry, and doc 05 I18N-001 criterion 4 requires no detector code path to be active.
  // It activates together with english.json — Appendix B §B.3, item I18N-003.
});

export default i18n;
